import { addDays, datesInclusive, daysBetween, monthEnd, monthStart, parseIsoDate, weekStart, weekday } from "./date.ts";
import { DomainError, type EffectiveRuleVersion, type IsoDate, type RepeatRule } from "./model.ts";

export interface PeriodBounds {
  readonly start: IsoDate;
  readonly end: IsoDate;
}

export interface DueContext {
  readonly outcomeAt: (date: IsoDate) => string | undefined;
}

export interface DueResult {
  readonly eligible: boolean;
  readonly due: boolean;
  readonly hitsBefore: number;
  readonly allocationsBefore: number;
  readonly eligibleTodayAndAfter: number;
  readonly quotaRemaining: number;
  readonly bounds: PeriodBounds;
}

export function assertRepeatRule(rule: RepeatRule): void {
  if (!Number.isInteger(rule.quota) || rule.quota < 1) {
    throw new DomainError("INVALID_REPEAT_RULE", "quota must be a positive integer");
  }
  if (rule.weekdays !== null) {
    if (rule.weekdays.length === 0 || new Set(rule.weekdays).size !== rule.weekdays.length) {
      throw new DomainError("INVALID_REPEAT_RULE", "weekdays must be null or a non-empty unique set");
    }
    if (rule.weekdays.some((day) => !Number.isInteger(day) || day < 0 || day > 6)) {
      throw new DomainError("INVALID_REPEAT_RULE", "weekdays must use Sunday=0 through Saturday=6");
    }
  }
  if (rule.period.kind === "rolling") {
    if (!Number.isInteger(rule.period.days) || rule.period.days < 1) {
      throw new DomainError("INVALID_REPEAT_RULE", "rolling days must be a positive integer");
    }
    if (rule.quota > rule.period.days) {
      throw new DomainError("INVALID_REPEAT_RULE", "rolling quota cannot exceed rolling days");
    }
    if (rule.part.kind !== "whole") {
      throw new DomainError("INVALID_REPEAT_RULE", "rolling rules must use the whole period");
    }
  } else if (rule.part.kind !== "whole" && (!Number.isInteger(rule.part.days) || rule.part.days < 1)) {
    throw new DomainError("INVALID_REPEAT_RULE", "partial-period days must be a positive integer");
  }
}

export function periodBounds(rule: RepeatRule, date: IsoDate): PeriodBounds {
  assertRepeatRule(rule);
  parseIsoDate(date);
  if (rule.period.kind === "rolling") {
    return { start: addDays(date, -(rule.period.days - 1)), end: date };
  }
  if (rule.period.kind === "week") {
    const start = weekStart(date);
    return { start, end: addDays(start, 6) };
  }
  return { start: monthStart(date), end: monthEnd(date) };
}

export function isRuleEligible(rule: RepeatRule, date: IsoDate): boolean {
  assertRepeatRule(rule);
  parseIsoDate(date);
  if (rule.weekdays !== null && !rule.weekdays.includes(weekday(date))) return false;
  if (rule.period.kind === "rolling" || rule.part.kind === "whole") return true;
  const bounds = periodBounds(rule, date);
  const index = daysBetween(bounds.start, date);
  const length = daysBetween(bounds.start, bounds.end) + 1;
  return rule.part.kind === "first" ? index < rule.part.days : index >= length - rule.part.days;
}

export function assertRuleCapacity(rule: RepeatRule, date: IsoDate): void {
  const bounds = periodBounds(rule, date);
  const capacity = datesInclusive(bounds.start, bounds.end)
    .filter((candidate) => isRuleEligible(rule, candidate)).length;
  if (rule.quota > capacity) {
    throw new DomainError("IMPOSSIBLE_REPEAT_RULE", `quota ${rule.quota} exceeds ${capacity} eligible slot(s) in the effective period`);
  }
}

function isHit(outcome: string | undefined): boolean {
  return outcome === "T" || outcome === "W";
}

export function dueStatus(rule: RepeatRule, date: IsoDate, context: DueContext): DueResult {
  const bounds = periodBounds(rule, date);
  if (!isRuleEligible(rule, date)) {
    return { eligible: false, due: false, hitsBefore: 0, allocationsBefore: 0, eligibleTodayAndAfter: 0, quotaRemaining: rule.quota, bounds };
  }

  if (rule.period.kind === "rolling") {
    let hitsBefore = 0;
    for (let offset = 1; offset < rule.period.days; offset += 1) {
      if (isHit(context.outcomeAt(addDays(date, -offset)))) hitsBefore += 1;
    }
    const quotaRemaining = Math.max(0, rule.quota - hitsBefore);
    return {
      eligible: true,
      due: quotaRemaining > 0,
      hitsBefore,
      allocationsBefore: hitsBefore,
      eligibleTodayAndAfter: 1,
      quotaRemaining,
      bounds,
    };
  }

  let hitsBefore = 0;
  let allocationsBefore = 0;
  let eligibleTodayAndAfter = 0;
  for (const candidate of datesInclusive(bounds.start, bounds.end)) {
    if (!isRuleEligible(rule, candidate)) continue;
    if (candidate < date) {
      const outcome = context.outcomeAt(candidate);
      if (isHit(outcome)) hitsBefore += 1;
      if (outcome === "T" || outcome === "W" || outcome === "C" || outcome === "M") allocationsBefore += 1;
    } else {
      eligibleTodayAndAfter += 1;
    }
  }
  const quotaRemaining = Math.max(0, rule.quota - allocationsBefore);
  return {
    eligible: true,
    due: quotaRemaining > 0 && eligibleTodayAndAfter <= quotaRemaining,
    hitsBefore,
    allocationsBefore,
    eligibleTodayAndAfter,
    quotaRemaining,
    bounds,
  };
}

export function ruleVersionOn(versions: readonly EffectiveRuleVersion[], date: IsoDate): EffectiveRuleVersion {
  parseIsoDate(date);
  const sorted = [...versions].sort((left, right) => left.effectiveFrom.localeCompare(right.effectiveFrom));
  let selected: EffectiveRuleVersion | undefined;
  for (const version of sorted) {
    parseIsoDate(version.effectiveFrom);
    if (version.effectiveToExclusive !== undefined && version.effectiveToExclusive !== null) {
      parseIsoDate(version.effectiveToExclusive);
      if (version.effectiveToExclusive <= version.effectiveFrom) {
        throw new DomainError("INVALID_RULE_RANGE", "rule effective end must follow its start");
      }
    }
    assertRepeatRule(version.rule);
    if (version.effectiveFrom > date) break;
    if (
      version.effectiveToExclusive === undefined ||
      version.effectiveToExclusive === null ||
      date < version.effectiveToExclusive
    ) {
      assertRuleCapacity(version.rule, date);
      selected = version;
    }
  }
  if (!selected) throw new DomainError("NO_EFFECTIVE_RULE", `No repeat rule is effective on ${date}`);
  return selected;
}
